import type { ComponentProps } from 'react'
import { classNames } from '~/utils/client/helpers'

type TableHeaderCellBaseProps = {
  isSiteReaderLabel?: boolean
  label: string
  align?: 'left' | 'center' | 'right'
  isFixed?: boolean
  isRounded?: boolean
}

type TableHeaderCellProps = TableHeaderCellBaseProps & ComponentProps<'th'>

export const TableHeaderCell = ({
  className,
  children,
  isSiteReaderLabel = false,
  align = 'left',
  label,
  isFixed = false,
  isRounded = false,
  ...props
}: TableHeaderCellProps) => {
  const dynamicClassName = classNames(
    isFixed
      ? 'flex sticky top-0 bg-surface-hover right-0 py-0 h-full items-center justify-center'
      : '',
    isRounded ? 'first:rounded-tl-lg last:rounded-tr-lg' : '',
    align === 'right' ? 'text-right' : '',
    align === 'center' ? 'text-center' : '',
    align === 'left' ? 'text-left' : ''
  )
  return (
    <th scope="col" className={classNames('px-4 py-3', dynamicClassName, className)} {...props}>
      {isSiteReaderLabel ? <span className="sr-only">{children}</span> : label || children}
    </th>
  )
}

export type TableHeaderBaseProps = {
  isRounded?: boolean
  cols?: TableHeaderCellProps[]
  isTransparent?: boolean
}

type TableHeaderProps = {
  renderOption?: (col: TableHeaderCellBaseProps, index: number) => React.ReactNode
} & TableHeaderBaseProps &
  ComponentProps<'thead'>

export const TableHeader = ({
  isRounded = false,
  isTransparent = false,
  cols,
  renderOption,
  className,
  children,
  ...props
}: TableHeaderProps) => {
  return (
    <thead
      className={classNames(
        'text-xs text-body uppercase',
        isTransparent ? 'bg-transparent' : 'bg-surface-hover ',
        className
      )}
      {...props}
    >
      <tr className="before:w-1 before:bg-transparent">
        {children
          ? children
          : cols?.map((col, index) => {
              if (renderOption) {
                return renderOption(col, index)
              }
              return (
                <TableHeaderCell
                  key={'header-cell-' + col.label + index}
                  isRounded={isRounded}
                  {...col}
                />
              )
            })}
      </tr>
    </thead>
  )
}
